import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChatContainer } from '@/components/chat/ChatContainer';
import { useUserStore } from '@/stores/userStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useChatStore } from '@/stores/chatStore';
import { runInfoAndScopesHandoff } from '@/stores/guidedSetup';
import { chat, connection } from '@/lib/api';
import { useBrowserStore } from '@/stores/browserStore';
import { useSetupStore } from '@/stores/setupStore';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { ZOOM_PORTAL_URL, ZOOM_S2S_CREATE_URL, ZOOM_SETUP_PROMPT, ZOOM_ACCESS_CHOICE, ZOOM_SCOPES_PENDING_MESSAGE, zoomAckText, zoomChoiceLabel } from '@/lib/zoomSetup';
import { useHarnessStatus } from '@/hooks/useHarnessStatus';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

type OAuthStatus = { type: 'success'; account: string } | { type: 'error'; message: string } | null;

export function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeConversationId } = useParams();
  const navigate = useNavigate();
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus>(null);
  // Set to the conversation id once its persisted messages have finished
  // loading — hydration waits for this so it can't race the load and re-inject
  // (and re-persist) the setup narration on every refresh.
  const [historyLoaded, setHistoryLoaded] = useState<string | null>(null);
  const { initialize: initUser, isInitialized: userInitialized } = useUserStore();
  const { checkConnection } = useConnectionStore();
  const { addAssistantMessage, addUserMessage, selectConversation, sendMessage, createConversation, setActiveConversation } = useChatStore();
  const currentConversationId = useChatStore((s) => s.currentConversationId);
  const messages = useChatStore((s) => s.messages);
  const openBrowserPanel = useBrowserStore((s) => s.openPanel);
  const setPendingNavigateUrl = useBrowserStore((s) => s.setPendingNavigateUrl);
  const setAgentControl = useBrowserStore((s) => s.setAgentControl);
  const setupConversationId = useSetupStore((s) => s.setupConversationId);
  const setSetupConversationId = useSetupStore((s) => s.setSetupConversationId);
  const accessLevel = useSetupStore((s) => s.accessLevel);
  const setAccessLevel = useSetupStore((s) => s.setAccessLevel);
  const setupLoaded = useSetupStore((s) => s.loaded);
  const connected = useSetupStore((s) => s.connected);
  const setupHandledRef = useRef(false);
  const writeHandledRef = useRef(false);
  const hydratedRef = useRef<string | null>(null);
  useHarnessStatus();

  useEffect(() => {
    initUser();
  }, [initUser]);

  useEffect(() => {
    if (userInitialized) {
      checkConnection();
    }
  }, [userInitialized, checkConnection]);

  // REST-style routing: /chat/:id loads that conversation. Keeps the active
  // conversation in the URL so it survives reload and is deep-linkable.
  // (Access is authorized server-side, scoped to the owning user.)
  useEffect(() => {
    if (!routeConversationId || !userInitialized || routeConversationId === currentConversationId) return;
    // Wait for the setup state to load so we correctly recognize the setup chat
    // (and take the client-rebuild path) instead of doing a racy backend load
    // that wipes the rehydrated transcript.
    if (!setupLoaded) return;
    if (routeConversationId === setupConversationId) {
      // Setup chat: confirm it still exists server-side (if history was cleared,
      // forget the stale localStorage instead of resurrecting it), then LOAD its
      // persisted transcript. Guided-setup messages now persist (appendMsg), so
      // the backend is the source of truth — we show the real history. Only a
      // genuinely empty thread falls through to the client rebuild (hydrate
      // effect below, gated on messages.length === 0).
      void (async () => {
        try {
          await chat.getConv(routeConversationId);
        } catch {
          setSetupConversationId(null);
          setAccessLevel(null);
          navigate('/chat', { replace: true });
          return;
        }
        await selectConversation(routeConversationId);
        // Load settled — the hydrate effect may now safely decide empty-rebuild
        // vs leave-real-history, without racing this async load.
        setHistoryLoaded(routeConversationId);
      })();
    } else {
      void Promise.resolve(selectConversation(routeConversationId)).then(() => setHistoryLoaded(routeConversationId));
    }
  }, [routeConversationId, userInitialized, setupLoaded, currentConversationId, setupConversationId, selectConversation, setActiveConversation, setSetupConversationId, setAccessLevel, navigate]);

  // Legacy deep-link via ?c=<id> — redirect to the canonical /chat/:id route.
  useEffect(() => {
    const convId = searchParams.get('c');
    if (convId && userInitialized) {
      navigate(`/chat/${convId}`, { replace: true });
    }
  }, [searchParams, userInitialized, navigate]);

  // Guided Zoom setup via ?setup=zoom (from Settings "Click here to setup zoom
  // connection"). Runs in a SINGLE dedicated chat: reuse it if it exists, else
  // create one. This effect only ensures we're IN that chat — the hydrate
  // effect below rebuilds the prompt/state. StrictMode's double-invoke is
  // guarded; the guard resets once the param clears so later clicks re-run.
  useEffect(() => {
    const isSetup = searchParams.get('setup') === 'zoom';
    if (!isSetup) { setupHandledRef.current = false; return; }
    // Wait for the server-side setup state so we reuse the existing setup chat
    // instead of spawning a duplicate.
    if (!userInitialized || !setupLoaded || setupHandledRef.current) return;
    setupHandledRef.current = true;
    setSearchParams({}, { replace: true });

    void (async () => {
      const exists = setupConversationId
        && useChatStore.getState().conversations.some((c) => c.id === setupConversationId);
      let id: string;
      if (exists) {
        id = setupConversationId!;
        // Load the persisted transcript, then mark history loaded so the hydrate
        // effect can decide (show history vs rebuild an empty thread) without
        // racing the load. route-sync early-returns here since the id already
        // matches, so this path must load + signal it itself.
        await selectConversation(id);
        setHistoryLoaded(id);
      } else {
        // Fresh setup: new (empty) conversation, reset the chosen access level.
        // Nothing to load, so signal history-loaded immediately so hydration runs.
        id = await createConversation();
        setSetupConversationId(id);
        setAccessLevel(null);
        setHistoryLoaded(id);
      }
      openBrowserPanel();
      // Reflect the setup chat in the URL so reloads land back here.
      navigate(`/chat/${id}`, { replace: true });
    })();
  }, [searchParams, setSearchParams, userInitialized, setupLoaded, navigate, setActiveConversation, selectConversation, createConversation, openBrowserPanel, setupConversationId, setSetupConversationId, setAccessLevel]);

  // Write-access enablement via ?setup=write (redirected from a plan that needs
  // write access, or scopes the app lacks). Enter the SAME starred setup chat, open
  // the browser panel, and kick off the guided write phase (WriteEnablePanel
  // renders + narrates). On success the write store opens the plan to approve it.
  useEffect(() => {
    const isWrite = searchParams.get('setup') === 'write';
    if (!isWrite) { writeHandledRef.current = false; return; }
    if (!userInitialized || !setupLoaded || writeHandledRef.current) return;
    writeHandledRef.current = true;
    setSearchParams({}, { replace: true });

    void (async () => {
      const exists = setupConversationId
        && useChatStore.getState().conversations.some((c) => c.id === setupConversationId);
      let id: string;
      if (exists) {
        id = setupConversationId!;
        await selectConversation(id);
        setHistoryLoaded(id);
      } else {
        id = await createConversation();
        setSetupConversationId(id);
        setHistoryLoaded(id);
      }
      openBrowserPanel();
      navigate(`/chat/${id}`, { replace: true });
      // Start (or restart) the enablement — WriteEnablePanel reflects progress.
      void useWriteEnableStore.getState().run();
    })();
  }, [searchParams, setSearchParams, userInitialized, setupLoaded, navigate, selectConversation, createConversation, openBrowserPanel, setupConversationId, setSetupConversationId]);

  // Rehydrate the setup chat whenever it becomes the active conversation
  // (CTA, sidebar click, or reload). The guided-setup messages live only on the
  // client, so we deterministically rebuild them from the persisted access
  // level — this is what makes the chat's history/state survive navigation.
  useEffect(() => {
    if (!currentConversationId || currentConversationId !== setupConversationId) {
      // Left the setup chat — allow re-hydration next time we enter it.
      hydratedRef.current = null;
      return;
    }
    // Wait for server state so we rebuild the correct step (e.g. don't show the
    // access prompt with buttons if the user already chose).
    if (!setupLoaded) return;
    // Wait for the persisted transcript to finish loading. Without this the
    // effect races selectConversation on an empty store and re-injects (and
    // re-persists) the narration on every refresh — the "keeps adding on hard
    // refresh" bug.
    if (historyLoaded !== currentConversationId) return;
    if (hydratedRef.current === currentConversationId) return;
    // Only rebuild once the backend load has settled into an empty thread; if
    // real messages exist we leave them alone — EXCEPT the parked-mid-flow
    // case: the transcript shows the app was created but we're not connected
    // and no credentials were captured (the capture failed and the retry
    // button wasn't persisted). Re-offer the retry affordance transiently.
    if (messages.length > 0) {
      hydratedRef.current = currentConversationId;
      const appWasCreated = messages.some((m) => m.role === 'assistant' && /is created\.$/.test(m.content.trim()));
      if (!connected && appWasCreated) {
        void (async () => {
          try {
            const apps = await connection.getAllOAuthSettings();
            if ((apps.settings ?? []).length === 0) {
              openBrowserPanel();
              await addAssistantMessage(
                'Looks like we parked mid-setup: your app exists on Zoom but I never managed to save its credentials. Open its **App Credentials** view in the browser and hit retry.',
                undefined,
                {
                  type: 'choice',
                  options: [
                    { id: 'zoom-setup-retry-capture-resume', label: 'Retry credential capture', value: 'zoom-setup:retry-capture', highlight: true },
                  ],
                },
                { once: true, persist: false },
              );
            }
          } catch { /* best-effort parked-state detection */ }
        })();
      }
      return;
    }
    hydratedRef.current = currentConversationId;

    void (async () => {
      // Already connected: the app exists on Zoom. Do NOT re-drive creation
      // (that spawns a duplicate app) — surface the remaining step (read
      // scopes) with the finale button: finishGuidedSetup is idempotent
      // (re-activate no-ops, identity re-captures, verify re-mints).
      if (connected) {
        openBrowserPanel();
        // Only reached when the thread is empty (no persisted history to show).
        // Transient (persist:false) so the real transcript, if it loads, wins.
        await addAssistantMessage(ZOOM_SCOPES_PENDING_MESSAGE, undefined, {
          type: 'choice',
          options: [
            { id: 'zoom-setup-scopes-added', label: "I've added the scopes — verify", value: 'zoom-setup:scopes-added', highlight: true },
          ],
        }, { once: true, persist: false });
        return;
      }
      // Not connected, but an S2S app was already provisioned (created + creds
      // captured — the durable signal is the oauth_app_settings row): resume
      // idempotently at the Information/scopes phase instead of re-creating.
      try {
        const apps = await connection.getAllOAuthSettings();
        if ((apps.settings ?? []).length > 0) {
          openBrowserPanel();
          await addAssistantMessage(
            'Picking up where we left off — your connection app already exists, so I\'ll finish configuring it.',
            undefined, undefined, { once: true, persist: false },
          );
          await runInfoAndScopesHandoff();
          return;
        }
      } catch { /* fall through to the fresh-start rebuild */ }
      // Step 1: the access-level prompt. Buttons only while no choice is made.
      await addAssistantMessage(ZOOM_SETUP_PROMPT, undefined, accessLevel ? undefined : ZOOM_ACCESS_CHOICE, { once: true });
      openBrowserPanel();
      if (accessLevel) {
        // Choice already made — show it as a user turn, then the acknowledgment,
        // and drive straight to the S2S builder (auth-gated). Already signed in
        // -> immediate takeover; otherwise Zoom bounces to login and the release
        // effect hands control back to sign in.
        await addUserMessage(zoomChoiceLabel(accessLevel), { once: true });
        setAgentControl(true);
        setPendingNavigateUrl(ZOOM_S2S_CREATE_URL);
        await addAssistantMessage(zoomAckText(accessLevel), undefined, undefined, { once: true });
      } else {
        // ZAC opens the portal — blue overlay while it navigates / the user
        // picks an access level in chat.
        setAgentControl(true);
        setPendingNavigateUrl(ZOOM_PORTAL_URL);
      }
    })();
  }, [currentConversationId, setupConversationId, setupLoaded, historyLoaded, messages, accessLevel, connected, addAssistantMessage, addUserMessage, openBrowserPanel, setAgentControl, setPendingNavigateUrl]);

  // Handle pre-filled query from answer pages via ?q=<question>
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && userInitialized) {
      setSearchParams({}, { replace: true });
      sendMessage(query);
    }
  }, [searchParams, setSearchParams, userInitialized, sendMessage]);

  // Handle OAuth callback
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const account = searchParams.get('account');
    const message = searchParams.get('message');

    if (oauth === 'success' && account) {
      setOauthStatus({ type: 'success', account });
      // Clear the query params
      setSearchParams({});
      // Refresh connection state
      checkConnection().then(() => {
        // Add a welcome message
        addAssistantMessage(`Connected to **${account}**! How can I help you configure your Zoom account?`, undefined, undefined, { once: true });
      });
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setOauthStatus(null), 5000);
      return () => clearTimeout(timer);
    } else if (oauth === 'error') {
      setOauthStatus({ type: 'error', message: message ?? 'OAuth connection failed' });
      // Clear the query params
      setSearchParams({});
      // Auto-dismiss after 10 seconds
      const timer = setTimeout(() => setOauthStatus(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, checkConnection, addAssistantMessage]);

  return (
    <div className="flex h-full flex-col bg-background text-foreground">

      {/* OAuth status banner */}
      {oauthStatus?.type === 'success' && (
        <div className="bg-green-900/30 border-b border-green-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Successfully connected to {oauthStatus.account}</span>
            </div>
            <button
              onClick={() => setOauthStatus(null)}
              className="text-green-400/70 hover:text-green-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {oauthStatus?.type === 'error' && (
        <div className="bg-red-900/30 border-b border-red-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">OAuth error: {oauthStatus.message}</span>
            </div>
            <button
              onClick={() => setOauthStatus(null)}
              className="text-red-400/70 hover:text-red-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-hidden">
        <ChatContainer />
      </main>
    </div>
  );
}
